import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BookFilters, BookStatus } from '../../types/Book';
import { bookService } from '../../services/bookService';
import './FilterDrawer.css';

interface Subject {
  subject_name: string;
  book_count: number;
}

interface FilterDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  filters: BookFilters;
  onFiltersChange: (filters: BookFilters) => void;
  onSearch: (query: string, filters: BookFilters) => void;
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
}

const FilterDrawer: React.FC<FilterDrawerProps> = ({
  isOpen,
  onClose,
  filters,
  onFiltersChange,
  onSearch,
  searchQuery,
  onSearchQueryChange
}) => {
  // Existing subjects state for dropdown
  const [subjects, setSubjects] = useState<string[]>([]);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [subjectsError, setSubjectsError] = useState<string | null>(null);
  const [subjectsFetched, setSubjectsFetched] = useState(false);
  const fetchingRef = React.useRef<boolean>(false);
  
  // New state for subjects browsing
  const [activeTab, setActiveTab] = useState<'filters' | 'subjects'>('filters');
  const [allSubjects, setAllSubjects] = useState<Subject[]>([]);
  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const [subjectsLoadingMore, setSubjectsLoadingMore] = useState(false);
  const [subjectsOffset, setSubjectsOffset] = useState(0);
  const [subjectsHasMore, setSubjectsHasMore] = useState(false);
  const [subjectsTotalCount, setSubjectsTotalCount] = useState(0);
  const [subjectsSearchQuery, setSubjectsSearchQuery] = useState('');
  const [subjectsSortBy, setSubjectsSortBy] = useState<'book_count' | 'subject_name'>('book_count');
  const subjectsLoadingRef = useRef<boolean>(false);

  // Fetch subjects for filter dropdown only once when first opened
  useEffect(() => {
    const fetchSubjects = async () => {
      // Prevent duplicate calls during React.StrictMode
      if (fetchingRef.current) {
        console.log('FilterDrawer: Already fetching subjects, skipping duplicate call');
        return;
      }
      
      fetchingRef.current = true;
      setLoadingSubjects(true);
      setSubjectsError(null);
      try {
        console.log('FilterDrawer: Fetching subjects...');
        const subjectsList = await bookService.getSubjectNames(100, 0, 'subject_name');
        
        // Ensure the currently selected subject is included in the list
        let finalSubjects = [...subjectsList];
        if (filters.subject && !subjectsList.includes(filters.subject)) {
          console.log('FilterDrawer: Adding selected subject to list:', filters.subject);
          finalSubjects = [filters.subject, ...subjectsList].sort();
        }
        
        setSubjects(finalSubjects);
        setSubjectsFetched(true);
        if (finalSubjects.length === 0) {
          setSubjectsError('No subjects found');
        }
      } catch (error) {
        console.error('Error fetching subjects:', error);
        setSubjectsError('Failed to load subjects');
        setSubjects([]);
      } finally {
        setLoadingSubjects(false);
        fetchingRef.current = false;
      }
    };

    if (isOpen && !subjectsFetched) {
      fetchSubjects();
    }
  }, [isOpen, subjectsFetched, filters.subject]);

  // Update subjects list when selected subject changes after initial fetch
  useEffect(() => {
    if (subjectsFetched && filters.subject && !subjects.includes(filters.subject)) {
      console.log('FilterDrawer: Adding new selected subject to existing list:', filters.subject);
      setSubjects(prevSubjects => [filters.subject!, ...prevSubjects].sort());
    }
  }, [filters.subject, subjects, subjectsFetched]);

  // Load all subjects for browsing with pagination
  const loadAllSubjects = useCallback(async (reset: boolean = true) => {
    if (subjectsLoadingRef.current) {
      console.log('FilterDrawer: Already loading subjects, skipping duplicate call');
      return;
    }
    
    try {
      console.log('FilterDrawer: Starting to load all subjects with sortBy:', subjectsSortBy, 'reset:', reset);
      subjectsLoadingRef.current = true;
      
      if (reset) {
        setSubjectsLoading(true);
        setSubjectsOffset(0);
        setSubjectsError(null);
      } else {
        setSubjectsLoadingMore(true);
      }
      
      const currentOffset = reset ? 0 : subjectsOffset;
      
      // Get subjects with book counts and pagination
      const result = await bookService.getSubjects(50, currentOffset, subjectsSortBy);
      console.log('FilterDrawer: Loaded subjects result:', result);
      
      if (reset) {
        setAllSubjects(result.subjects);
      } else {
        setAllSubjects(prevSubjects => [...prevSubjects, ...result.subjects]);
      }
      
      // Update pagination state
      if (result.pagination) {
        setSubjectsHasMore(result.pagination.hasMore);
        setSubjectsTotalCount(result.pagination.total);
        setSubjectsOffset(result.pagination.nextOffset || currentOffset + result.subjects.length);
      } else {
        setSubjectsHasMore(false);
        setSubjectsTotalCount(result.subjects.length);
      }
      
    } catch (err) {
      console.error('Error loading all subjects:', err);
      setSubjectsError('Failed to load subjects');
    } finally {
      setSubjectsLoading(false);
      setSubjectsLoadingMore(false);
      subjectsLoadingRef.current = false;
    }
  }, [subjectsSortBy, subjectsOffset]);

  const loadMoreAllSubjects = useCallback(() => {
    if (!subjectsHasMore || subjectsLoadingRef.current) return;
    loadAllSubjects(false);
  }, [subjectsHasMore, loadAllSubjects]);

  // Load subjects when tab switches to subjects or sort changes
  useEffect(() => {
    if (activeTab === 'subjects' && allSubjects.length === 0) {
      loadAllSubjects(true);
    }
  }, [activeTab, loadAllSubjects, allSubjects.length]);

  useEffect(() => {
    if (activeTab === 'subjects') {
      console.log('FilterDrawer: Subject sort changed, reloading subjects');
      loadAllSubjects(true);
    }
  }, [subjectsSortBy, activeTab, loadAllSubjects]);

  const handleSubjectClick = (subjectName: string) => {
    // Apply subject filter and switch to filters tab
    const newFilters = {
      ...filters,
      subject: subjectName
    };
    onFiltersChange(newFilters);
    setActiveTab('filters');
  };

  const handleFilterChange = (key: keyof BookFilters, value: any) => {
    const newFilters = {
      ...filters,
      [key]: value || undefined
    };
    onFiltersChange(newFilters);
  };

  const handleApplyFilters = () => {
    onSearch(searchQuery, filters);
    onClose();
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      sortBy: 'dateAdded' as const,
      sortOrder: 'desc' as const
    };
    onFiltersChange(clearedFilters);
    onSearchQueryChange('');
    onSearch('', clearedFilters);
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSearchQueryChange(e.target.value);
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleApplyFilters();
    }
  };

  // Prevent body scroll when drawer is open on mobile
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div className="filter-drawer-overlay" onClick={onClose} />
      )}
      
      {/* Drawer */}
      <div className={`filter-drawer ${isOpen ? 'open' : ''}`}>
        <div className="filter-drawer-header">
          <h3>🔍 Search & Filter Books</h3>
          <button className="close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="filter-drawer-tabs">
          <button 
            className={`tab-btn ${activeTab === 'filters' ? 'active' : ''}`}
            onClick={() => setActiveTab('filters')}
          >
            🔍 Filters
          </button>
          <button 
            className={`tab-btn ${activeTab === 'subjects' ? 'active' : ''}`}
            onClick={() => setActiveTab('subjects')}
          >
            📋 Browse Subjects
          </button>
        </div>

        <div className="filter-drawer-content">
          {/* Filters Tab Content */}
          {activeTab === 'filters' && (
            <>
              {/* Search */}
          <div className="filter-section">
            <label htmlFor="search-input">Search</label>
            <input
              id="search-input"
              type="text"
              placeholder="Search by title, author, or description..."
              value={searchQuery}
              onChange={handleSearchInputChange}
              onKeyPress={handleSearchKeyPress}
              className="filter-input"
            />
          </div>

          {/* Status Filter */}
          <div className="filter-section">
            <label htmlFor="status-filter">Reading Status</label>
            <select
              id="status-filter"
              value={filters.status || ''}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="filter-select"
            >
              <option value="">All Status</option>
              <option value={BookStatus.WANT_TO_READ}>Want to Read</option>
              <option value={BookStatus.CURRENTLY_READING}>Currently Reading</option>
              <option value={BookStatus.READ}>Read</option>
              <option value={BookStatus.DNF}>Did Not Finish</option>
            </select>
          </div>

          {/* Author Filter */}
          <div className="filter-section">
            <label htmlFor="author-filter">Author</label>
            <input
              id="author-filter"
              type="text"
              placeholder="Filter by author..."
              value={filters.author || ''}
              onChange={(e) => handleFilterChange('author', e.target.value)}
              className="filter-input"
            />
          </div>

          {/* Subject/Genre Filter */}
          <div className="filter-section">
            <label htmlFor="subject-filter">Subject/Genre</label>
            <select
              id="subject-filter"
              value={filters.subject || ''}
              onChange={(e) => handleFilterChange('subject', e.target.value)}
              className="filter-select"
              disabled={loadingSubjects || subjectsError !== null}
            >
              <option value="">All Subjects</option>
              {subjects.map(subject => (
                <option key={subject} value={subject}>{subject}</option>
              ))}
            </select>
            {loadingSubjects && <span className="loading-text">Loading subjects...</span>}
            {subjectsError && <span className="error-text">{subjectsError}</span>}
          </div>

          {/* Language Filter */}
          <div className="filter-section">
            <label htmlFor="language-filter">Language</label>
            <select
              id="language-filter"
              value={filters.language || ''}
              onChange={(e) => handleFilterChange('language', e.target.value)}
              className="filter-select"
            >
              <option value="">All Languages</option>
              <option value="en">English</option>
              <option value="fr">French</option>
              <option value="es">Spanish</option>
              <option value="de">German</option>
              <option value="it">Italian</option>
              <option value="pt">Portuguese</option>
              <option value="ru">Russian</option>
              <option value="ja">Japanese</option>
              <option value="zh">Chinese</option>
            </select>
          </div>

          {/* Publication Year Filter */}
          <div className="filter-section">
            <label htmlFor="year-filter">Publication Year</label>
            <input
              id="year-filter"
              type="number"
              placeholder="e.g. 1925"
              value={filters.publication_year || ''}
              onChange={(e) => handleFilterChange('publication_year', e.target.value)}
              className="filter-input"
              min="1000"
              max="2024"
            />
          </div>

          {/* Rating Filter */}
          <div className="filter-section">
            <label htmlFor="rating-filter">Minimum Rating</label>
            <select
              id="rating-filter"
              value={filters.minRating || ''}
              onChange={(e) => handleFilterChange('minRating', e.target.value ? parseInt(e.target.value) : undefined)}
              className="filter-select"
            >
              <option value="">Any Rating</option>
              <option value="1">1+ Stars</option>
              <option value="2">2+ Stars</option>
              <option value="3">3+ Stars</option>
              <option value="4">4+ Stars</option>
              <option value="5">5 Stars</option>
            </select>
          </div>

          {/* Sort Options */}
          <div className="filter-section">
            <label htmlFor="sort-filter">Sort By</label>
            <select
              id="sort-filter"
              value={filters.sortBy || 'dateAdded'}
              onChange={(e) => handleFilterChange('sortBy', e.target.value)}
              className="filter-select"
            >
              <option value="dateAdded">Date Added</option>
              <option value="title">Title</option>
              <option value="author">Author</option>
              <option value="rating">Rating</option>
              <option value="publicationDate">Publication Date</option>
              <option value="position_slot">Position Slot</option>
            </select>
          </div>

          {/* Sort Order */}
          <div className="filter-section">
            <label htmlFor="sort-order-filter">Sort Order</label>
            <select
              id="sort-order-filter"
              value={filters.sortOrder || 'desc'}
              onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
              className="filter-select"
            >
              <option value="desc">Newest First (Descending)</option>
              <option value="asc">Oldest First (Ascending)</option>
            </select>
          </div>
            </>
          )}

          {/* Subjects Tab Content */}
          {activeTab === 'subjects' && (
            <div className="subjects-tab-content">
              {/* Search and Sort Controls for Subjects */}
              <div className="subjects-controls">
                <div className="subjects-search-box">
                  <input
                    type="text"
                    placeholder="Search subjects..."
                    value={subjectsSearchQuery}
                    onChange={(e) => setSubjectsSearchQuery(e.target.value)}
                    className="subjects-search-input"
                  />
                </div>
                
                <div className="subjects-sort-controls">
                  <label htmlFor="subjects-sort-select">Sort by:</label>
                  <select
                    id="subjects-sort-select"
                    value={subjectsSortBy}
                    onChange={(e) => setSubjectsSortBy(e.target.value as 'book_count' | 'subject_name')}
                    className="subjects-sort-select"
                  >
                    <option value="book_count">Book Count</option>
                    <option value="subject_name">Subject Name</option>
                  </select>
                </div>
              </div>

              {/* Loading State */}
              {subjectsLoading && (
                <div className="subjects-loading-state">
                  <div className="loading-spinner"></div>
                  <p>📋 Loading subjects...</p>
                </div>
              )}

              {/* Error State */}
              {subjectsError && (
                <div className="subjects-error-state">
                  <p>❌ {subjectsError}</p>
                  <button onClick={() => loadAllSubjects(true)}>Retry</button>
                </div>
              )}

              {/* Subjects Grid */}
              {!subjectsLoading && !subjectsError && (
                <>
                  <div className="subjects-grid">
                    {allSubjects
                      .filter(subject =>
                        subject.subject_name.toLowerCase().includes(subjectsSearchQuery.toLowerCase())
                      )
                      .map(subject => (
                        <div
                          key={subject.subject_name}
                          className="subject-card"
                          onClick={() => handleSubjectClick(subject.subject_name)}
                        >
                          <div className="subject-name">{subject.subject_name}</div>
                          <div className="subject-count">{subject.book_count} books</div>
                        </div>
                      ))}
                  </div>

                  {/* Load More Button */}
                  {subjectsHasMore && !subjectsLoading && (
                    <div className="subjects-load-more-section">
                      <button 
                        onClick={loadMoreAllSubjects}
                        disabled={subjectsLoadingMore}
                        className="subjects-load-more-button"
                      >
                        {subjectsLoadingMore ? 'Loading more subjects...' : 'Load More Subjects'}
                      </button>
                    </div>
                  )}

                  {/* Results Count */}
                  <div className="subjects-results-count">
                    Showing {allSubjects.filter(subject =>
                      subject.subject_name.toLowerCase().includes(subjectsSearchQuery.toLowerCase())
                    ).length} of {allSubjects.length} subjects
                    {subjectsTotalCount > 0 && allSubjects.length < subjectsTotalCount && (
                      <span className="subjects-total-available"> (Total available: {subjectsTotalCount})</span>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Actions - Different for each tab */}
        {activeTab === 'filters' && (
          <div className="filter-drawer-actions">
            <button 
              className="clear-btn"
              onClick={handleClearFilters}
            >
              Clear All
            </button>
            <button 
              className="apply-btn"
              onClick={handleApplyFilters}
            >
              Apply Filters
            </button>
          </div>
        )}
        
        {activeTab === 'subjects' && (
          <div className="filter-drawer-actions">
            <button 
              className="close-btn-action"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default FilterDrawer;
